import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, User, Heart, ShoppingCart, Menu, X } from 'lucide-react';
import { useShop } from '../lib/shop';

const NAV = [
  { to: '/shop', label: 'Shop All', end: true },
  { to: '/shop/men', label: 'Men' },
  { to: '/shop/women', label: 'Women' },
  { to: '/shop/genz', label: 'Gen-Z Tees' },
  { to: '/shop/ethnic', label: 'Kurtas & Sarees' },
  { to: '/shop/heritage', label: 'Historical Designs' },
  { to: '/shop/storytelling', label: 'Storytelling Premium' },
  { to: '/shop/limited', label: 'Limited Edition' },
  { to: '/our-story', label: 'Our Story' },
];

const SALE_TEXT = ['SALE SALE SALE', 'UP TO 80% OFF', 'FREE DELIVERY ALL OVER INDIA ON ₹799+', 'BUY 1 GET 1 FREE ON ₹999', 'BUY 2 GET 1 FREE ON ₹3,999', 'GST INCLUDED · NO PLATFORM FEE · NO PROCESSING FEE'];

const SaleStrip = () => (
  <div className="bg-[#E11D48] text-white overflow-hidden" data-testid="sale-strip">
    <div className="si-marquee flex w-max py-1.5">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0" aria-hidden={k === 1}>
          {SALE_TEXT.map((t) => <span key={t} className="px-6 text-xs md:text-sm font-extrabold tracking-wide whitespace-nowrap">★ {t}</span>)}
        </div>
      ))}
    </div>
  </div>
);

const slug = (s) => s.toLowerCase().replace(/\s/g, '-');
const currentUser = () => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } };

const Logo = () => (
  <span className="flex flex-col items-start" data-testid="brand-wordmark">
    <img src="/scholar-logo-mark.png" alt="Scholar Industry" className="h-12 md:h-[74px] w-auto object-contain" />
    <span className="mt-0.5 text-xs md:text-base font-bold italic text-[var(--si-navy)] leading-none whitespace-nowrap" data-testid="brand-tagline">Every Design Has a Story for You</span>
  </span>
);

const SearchBox = ({ testid }) => {
  const [q, setQ] = useState('');
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); if (q.trim()) nav(`/shop?q=${encodeURIComponent(q.trim())}`); };
  return (
    <form onSubmit={submit} className="flex w-full" data-testid={`${testid}-form`}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for shirts, dresses, heritage tees and more"
        data-testid={testid}
        className="flex-1 min-w-0 h-11 px-4 bg-[#F0F4FA] border border-[#D5DCE8] rounded-l-md text-sm text-[var(--si-black)] placeholder-[#6B7280] focus:outline-none focus:bg-white focus:border-[var(--si-blue)]" />
      <button type="submit" aria-label="Search" data-testid={`${testid}-submit`}
        className="h-11 px-4 rounded-r-md bg-[var(--si-yellow)] hover:bg-[#F08C00] text-white transition-colors">
        <Search className="w-5 h-5" />
      </button>
    </form>
  );
};

const IconLink = ({ to, icon: Icon, label, count, testid }) => (
  <Link to={to} className="relative flex flex-col md:flex-row items-center gap-1 md:gap-2 text-[var(--si-black)] hover:text-[var(--si-blue)] transition-colors" data-testid={testid}>
    <span className="relative">
      <Icon className="w-6 h-6" />
      {count > 0 && (
        <span className="absolute -top-2 -right-2 text-[10px] bg-[var(--si-orange)] text-white rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold" data-testid={`${testid}-count`}>
          {count}
        </span>
      )}
    </span>
    <span className="hidden md:inline text-sm font-semibold">{label}</span>
  </Link>
);

export const ShopNavbar = () => {
  const { cart, wishlistSlugs } = useShop();
  const [open, setOpen] = useState(false);
  const user = currentUser();
  const cartCount = (cart?.items || []).reduce((n, it) => n + (it.qty || 1), 0);

  return (
    <>
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_4px_rgba(0,0,0,0.08)]" data-testid="shop-header">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 md:py-3 flex items-center gap-3 md:gap-8">
          <button onClick={() => setOpen(true)} className="md:hidden text-[var(--si-black)]" aria-label="Open menu" data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="flex-shrink-0" data-testid="brand-link"><Logo /></Link>
          <div className="hidden md:block flex-1 max-w-2xl"><SearchBox testid="search-input" /></div>
          <div className="ml-auto flex items-center gap-4 md:gap-7">
            <IconLink to={user ? '/account' : '/login'} icon={User} label={user ? (user.name || 'Account').split(' ')[0] : 'Login'} testid="nav-account" />
            <IconLink to="/wishlist" icon={Heart} label="Wishlist" count={wishlistSlugs.length} testid="nav-wishlist" />
            <IconLink to="/cart" icon={ShoppingCart} label="Cart" count={cartCount} testid="nav-cart" />
          </div>
        </div>
        <div className="md:hidden px-4 pb-3"><SearchBox testid="search-input-mobile" /></div>
        <nav className="bg-[var(--si-navy)]" data-testid="category-strip">
          <div className="max-w-7xl mx-auto px-4 md:px-6 flex gap-6 md:gap-8 si-scroll-x whitespace-nowrap">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${slug(n.label)}`}
                className={({ isActive }) => `py-2.5 text-sm font-semibold border-b-2 transition-colors ${isActive ? 'text-white border-[var(--si-yellow)]' : 'text-white/90 border-transparent hover:text-white hover:border-white/60'}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
        <SaleStrip />
      </header>
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[82%] max-w-xs bg-white shadow-xl si-rise">
            <div className="bg-[var(--si-navy)] text-white px-5 py-4 flex items-center justify-between">
              <span className="font-semibold">{user ? `Hello, ${(user.name || '').split(' ')[0]}` : 'Hello, sign in'}</span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close"><X className="w-6 h-6" /></button>
            </div>
            <nav className="flex flex-col py-2">
              {[...NAV, { to: user ? '/account' : '/login', label: user ? 'My Account' : 'Login / Sign up' }].map((n) => (
                <Link key={n.to + n.label} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${slug(n.label)}`}
                  className="px-5 py-3 text-base font-medium text-[var(--si-black)] border-b border-[var(--si-line)] hover:bg-[var(--si-bg-soft)]">{n.label}</Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default ShopNavbar;
